import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Zap, ArrowLeft, DollarSign, Target,
  Sparkles, Image as ImageIcon, AlertCircle,
  ShoppingCart, UserPlus, MousePointer, Check, Info
} from "lucide-react";
import CloudinaryUpload from "../components/CloudinaryUpload";

const CampaignCreate = () => {
  const navigate = useNavigate();
  useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const [niches, setNiches] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [audienceTypes, setAudienceTypes] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    media_urls: [],
    target_niches: [],
    target_audience_types: [],
    target_platforms: [],
    goal: "customers",
    cost_per_result: "",
    budget_cap: ""
  });

  const goals = [
    {
      id: "customers",
      label: "Customers",
      description: "Pay when creators bring you paying customers",
      icon: ShoppingCart,
      example: "€10"
    },
    {
      id: "leads",
      label: "Leads",
      description: "Pay when creators bring you signups or inquiries",
      icon: UserPlus,
      example: "€2"
    },
    {
      id: "traffic",
      label: "Clicks",
      description: "Pay when creators drive visitors to your site",
      icon: MousePointer,
      example: "€0.50"
    }
  ];

  // All API calls use httpOnly cookies (withCredentials: true)
  const axiosConfig = { withCredentials: true };

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [nichesRes, platformsRes, audienceRes] = await Promise.all([
          axios.get(`${API}/niches`),
          axios.get(`${API}/platforms`),
          axios.get(`${API}/audience-types`)
        ]);
        setNiches(nichesRes.data.niches);
        setPlatforms(platformsRes.data.platforms);
        setAudienceTypes(audienceRes.data.audience_types);
      } catch (err) {
        console.error("Error fetching options:", err);
      }
    };
    fetchOptions();
  }, []);

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const handleMediaUploadComplete = (urls) => {
    setFormData(prev => ({ ...prev, media_urls: urls }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        media_urls: formData.media_urls,
        target_niches: formData.target_niches,
        target_audience_types: formData.target_audience_types,
        target_platforms: formData.target_platforms,
        goal: formData.goal,
        cost_per_result: formData.cost_per_result ? parseFloat(formData.cost_per_result) : null,
        budget_cap: formData.budget_cap ? parseFloat(formData.budget_cap) : null
      };

      await axios.post(`${API}/campaigns`, payload, axiosConfig);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  const selectedGoal = goals.find(g => g.id === formData.goal);
  const goalLabel = formData.goal === "customers" ? "customer" : formData.goal === "leads" ? "lead" : "click";

  const isStep1Valid = formData.title.trim() && formData.description.trim();
  const isStep2Valid = formData.goal && formData.cost_per_result && formData.budget_cap;

  return (
    <div className="min-h-screen bg-[#05060D] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Create Campaign</h1>
            <p className="text-gray-500 text-sm">Set up a performance-based creator campaign</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2].map((s) => (
            <React.Fragment key={s}>
              <button
                onClick={() => s < step && setStep(s)}
                disabled={s > step}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step === s
                    ? "bg-white text-black"
                    : step > s
                    ? "bg-cyan-500/20 text-cyan-400"
                    : "bg-white/10 text-gray-500"
                }`}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </button>
              {s < 2 && (
                <div className={`flex-1 h-1 rounded-full ${step > s ? "bg-cyan-500/50" : "bg-white/10"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Step 1: Campaign Details */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="glass-card rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                Campaign Details
              </h2>
              <p className="text-gray-500 text-sm mb-6">Tell creators about your campaign</p>

              <div className="space-y-5">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Campaign Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Summer Sale Promotion"
                    data-testid="campaign-title"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your offer and what you want creators to promote..."
                    rows={4}
                    data-testid="campaign-description"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none resize-none"
                  />
                </div>

                {/* Media Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-cyan-400" />
                      Campaign Media
                      <span className="text-gray-500 font-normal">(Optional)</span>
                    </div>
                  </label>
                  <CloudinaryUpload
                    onUploadComplete={handleMediaUploadComplete}
                    existingUrls={formData.media_urls}
                    maxFiles={5}
                  />
                </div>
              </div>
            </div>

            {/* Target Audience - Collapsed */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                Target Audience
              </h2>
              <p className="text-gray-500 text-sm mb-6">Help match your campaign with the right creators</p>

              <div className="space-y-5">
                {/* Niches */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Select Niches
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {niches.map((niche) => (
                      <button
                        key={niche}
                        onClick={() => toggleArrayItem("target_niches", niche)}
                        data-testid={`niche-${niche}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          formData.target_niches.includes(niche)
                            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                            : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/30"
                        }`}
                      >
                        {niche}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Platforms */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Target Platforms
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {platforms.map((platform) => (
                      <button
                        key={platform}
                        onClick={() => toggleArrayItem("target_platforms", platform)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          formData.target_platforms.includes(platform)
                            ? "bg-purple-500/20 text-purple-400 border border-purple-500/50"
                            : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/30"
                        }`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Audience Types */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Audience Age Groups
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {audienceTypes.map((audience) => (
                      <button
                        key={audience}
                        onClick={() => toggleArrayItem("target_audience_types", audience)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          formData.target_audience_types.includes(audience)
                            ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                            : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/30"
                        }`}
                      >
                        {audience}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!isStep1Valid}
              data-testid="next-step-btn"
              className="w-full py-4 rounded-xl bg-white text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Continue to Budget
            </button>
          </div>
        )}

        {/* Step 2: Campaign Setup (Goal, Reward, Budget) */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Campaign Setup Header */}
            <div className="glass-card rounded-2xl p-6 bg-gradient-to-br from-cyan-500/5 to-purple-500/5 border border-cyan-500/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Campaign Setup</h2>
                  <p className="text-gray-400 text-sm">Define how creators get paid</p>
                </div>
              </div>
              <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-sm text-gray-300">
                  <span className="text-cyan-400 font-semibold">How it works:</span> You set a goal and reward. Creators earn up to 80% of the reward when they deliver verified results. You only pay for real results.
                </p>
              </div>
            </div>

            {/* Campaign Goal */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-cyan-400" />
                  Campaign Goal
                </h3>
                <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">Step 1 of 3</span>
              </div>
              <p className="text-gray-400 text-sm mb-6">
                Choose what creators are rewarded for
              </p>

              <div className="grid gap-3">
                {goals.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => setFormData({ ...formData, goal: goal.id })}
                    data-testid={`goal-${goal.id}`}
                    className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-4 ${
                      formData.goal === goal.id
                        ? "bg-cyan-500/10 border-2 border-cyan-500/50"
                        : "bg-white/5 border-2 border-transparent hover:border-white/20"
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      formData.goal === goal.id ? "bg-cyan-500/20" : "bg-white/10"
                    }`}>
                      <goal.icon className={`w-6 h-6 ${
                        formData.goal === goal.id ? "text-cyan-400" : "text-gray-400"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`font-semibold ${
                        formData.goal === goal.id ? "text-white" : "text-gray-300"
                      }`}>
                        {goal.label}
                      </span>
                      <p className="text-sm text-gray-500 mt-0.5">{goal.description}</p>
                    </div>
                    {formData.goal === goal.id && (
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Reward Per Result */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Reward Per Result
                </h3>
                <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">Step 2 of 3</span>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                The amount paid per verified {goalLabel}
              </p>

              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-xl">€</span>
                <input
                  type="number"
                  value={formData.cost_per_result}
                  onChange={(e) => setFormData({ ...formData, cost_per_result: e.target.value })}
                  placeholder={selectedGoal?.example || "0"}
                  data-testid="cost-per-result"
                  min="0"
                  step="0.01"
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white text-2xl font-bold placeholder-gray-500 focus:border-green-400 focus:outline-none"
                />
              </div>
              <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-sm text-green-400">
                  <span className="font-semibold">Example:</span> €{selectedGoal?.example?.replace('€', '') || '5'} per verified {goalLabel}
                </p>
              </div>
            </div>

            {/* Campaign Budget */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                  Campaign Budget
                </h3>
                <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">Step 3 of 3</span>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                The maximum total campaign spend
              </p>

              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-xl">€</span>
                <input
                  type="number"
                  value={formData.budget_cap}
                  onChange={(e) => setFormData({ ...formData, budget_cap: e.target.value })}
                  placeholder="50"
                  data-testid="budget-cap"
                  min="0"
                  step="1"
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white text-2xl font-bold placeholder-gray-500 focus:border-purple-400 focus:outline-none"
                />
              </div>
              <div className="mt-3 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-sm text-purple-400">
                  <span className="font-semibold">Example:</span> €50 total budget — campaign pauses when reached
                </p>
              </div>
            </div>

            {/* Budget Preview */}
            {formData.cost_per_result && formData.budget_cap && parseFloat(formData.cost_per_result) > 0 && (
              <div className="glass-card rounded-2xl p-6 bg-gradient-to-br from-cyan-500/5 to-purple-500/5 border border-cyan-500/20">
                <h3 className="text-sm font-semibold text-white mb-4">Campaign Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Estimated {formData.goal}</span>
                    <span className="text-white font-bold text-lg">
                      ~{Math.floor(parseFloat(formData.budget_cap) / parseFloat(formData.cost_per_result))}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Creator earnings (80%)</span>
                    <span className="text-cyan-400 font-semibold">
                      €{(parseFloat(formData.budget_cap) * 0.8).toFixed(0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Platform fee (20%)</span>
                    <span className="text-gray-400 font-semibold">
                      €{(parseFloat(formData.budget_cap) * 0.2).toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-4 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !isStep2Valid}
                data-testid="submit-campaign-btn"
                className="flex-1 py-4 rounded-xl bg-white text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Create Campaign
                  </>
                )}
              </button>
            </div>

            {/* Help Text */}
            <p className="text-center text-xs text-gray-500">
              You can edit campaign details after creation
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignCreate;
